/*
  assets/bf-training-auth.js
  Customer Account API sign-in for BullFit Training.

  New customer accounts, public OAuth client, authorization code + PKCE (S256).
  There is NO client secret: a public client cannot hold one, which is exactly
  why PKCE exists — the code_verifier proves the app that redeemed the code is
  the same one that started the flow.

  Why not the app proxy's logged_in_customer_id: under new customer accounts it
  is frequently empty for a genuinely signed-in customer, so it cannot be relied
  on to identify a member. It stays useful as corroboration, never as authority.

  Token storage: sessionStorage. It clears when the tab closes and is not shared
  with other tabs. It is still readable by script on this origin, which is the
  accepted trade-off for a public client — so the ACCESS TOKEN IS NEVER THE
  AUTHORITY. The server re-verifies every token and takes the customer id from
  the verified `sub` claim, never from anything the client asserts.
*/
(function (global) {
  'use strict';

  var KEY_VERIFIER = 'bf_tr_pkce_verifier';
  var KEY_STATE = 'bf_tr_oauth_state';
  var KEY_TOKENS = 'bf_tr_tokens';
  var KEY_RETURN = 'bf_tr_return_to';

  function store() {
    try { return global.sessionStorage; } catch (e) { return null; }
  }
  function put(k, v) { var s = store(); if (s) { try { s.setItem(k, v); } catch (e) {} } }
  function get(k) { var s = store(); if (!s) return null; try { return s.getItem(k); } catch (e) { return null; } }
  function drop(k) { var s = store(); if (s) { try { s.removeItem(k); } catch (e) {} } }

  function b64url(bytes) {
    var s = '';
    var arr = new Uint8Array(bytes);
    for (var i = 0; i < arr.length; i++) s += String.fromCharCode(arr[i]);
    return global.btoa(s).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
  }
  function randomUrlSafe(n) {
    var a = new Uint8Array(n);
    global.crypto.getRandomValues(a);
    return b64url(a);
  }
  async function challengeFor(verifier) {
    var digest = await global.crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier));
    return b64url(digest);
  }

  /* --- tokens ------------------------------------------------------------ */

  function readTokens() {
    var raw = get(KEY_TOKENS);
    if (!raw) return null;
    try {
      var t = JSON.parse(raw);
      if (!t || !t.access_token) return null;
      return t;
    } catch (e) { return null; }
  }

  /* `expires_at` is derived from the response's `expires_in`, which describes the
     ACCESS token. It does NOT describe the id_token, whose own `exp` is much
     sooner — treating one as the other made isSignedIn() report a live session
     while every API call 401'd. */
  function isExpired(tokens, skewSeconds) {
    if (!tokens || !tokens.expires_at) return true;
    return Date.now() >= (tokens.expires_at - (skewSeconds || 60) * 1000);
  }

  function saveTokens(raw) {
    var t = {
      access_token: raw.access_token,
      id_token: raw.id_token,
      refresh_token: raw.refresh_token,
      // Absolute expiry: an interval is meaningless once it is stored.
      expires_at: Date.now() + (Number(raw.expires_in || 0) * 1000),
    };
    put(KEY_TOKENS, JSON.stringify(t));
    return t;
  }

  /** Decode a JWT payload for DISPLAY only. Never a security decision. */
  function readIdTokenClaims(idToken) {
    if (!idToken) return null;
    var parts = String(idToken).split('.');
    if (parts.length !== 3) return null;
    try {
      var p = parts[1].replace(/-/g, '+').replace(/_/g, '/');
      while (p.length % 4) p += '=';
      return JSON.parse(global.atob(p));
    } catch (e) { return null; }
  }

  /* --- flow -------------------------------------------------------------- */

  function Auth(config) {
    this.cfg = config;
  }

  /* A SIGNED-IN MEMBER WITH A STALE ACCESS TOKEN IS STILL SIGNED IN.
     This used to return false the moment the ACCESS token expired, ignoring
     the refresh token sitting beside it — the one accessToken() already uses
     to renew silently. The result, seen four times on a phone: navigating
     between screens mid-questionnaire dropped to "Sign in to begin", and a
     single tap brought the session straight back with no email and no code,
     because nothing had actually ended.

     A refresh token is not a promise that renewal will work, so the other half
     of this lives in refresh(): when the provider REFUSES the token, the
     stored session is cleared and this goes honestly false. What must not
     happen is claiming a session we cannot renew — that trades a needless
     sign-in wall for an app that fails every call instead. */
  Auth.prototype.isSignedIn = function () {
    var t = readTokens();
    if (!t) return false;
    if (!isExpired(t, 0)) return true;
    return !!t.refresh_token;
  };

  Auth.prototype.tokens = readTokens;

  Auth.prototype.signIn = async function (returnTo) {
    var verifier = randomUrlSafe(64);
    var state = randomUrlSafe(16);
    var nonce = randomUrlSafe(16);
    put(KEY_VERIFIER, verifier);
    put(KEY_STATE, state);
    if (returnTo) put(KEY_RETURN, returnTo);

    var challenge = await challengeFor(verifier);
    var q = new URLSearchParams({
      client_id: this.cfg.clientId,
      response_type: 'code',
      redirect_uri: this.cfg.callbackUri,
      scope: this.cfg.scope || 'openid email customer-account-api:full',
      state: state,
      nonce: nonce,
      code_challenge: challenge,
      code_challenge_method: 'S256',
    });
    global.location.assign(this.cfg.authorizationEndpoint + '?' + q.toString());
  };

  /**
   * Handle the redirect back from Shopify.
   * Returns { ok, reason } — the caller renders the state; this never throws
   * a bare error at the member.
   */
  Auth.prototype.handleCallback = async function () {
    var params = new URLSearchParams(global.location.search);
    var err = params.get('error');
    if (err) return { ok: false, reason: err };

    var code = params.get('code');
    var state = params.get('state');
    if (!code) return { ok: false, reason: 'no_code' };

    var expected = get(KEY_STATE);
    // CSRF: a code delivered with a state we did not issue is rejected outright.
    if (!expected || state !== expected) return { ok: false, reason: 'state_mismatch' };
    drop(KEY_STATE);

    var verifier = get(KEY_VERIFIER);
    if (!verifier) return { ok: false, reason: 'missing_verifier' };
    drop(KEY_VERIFIER);

    var body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: this.cfg.clientId,
      redirect_uri: this.cfg.callbackUri,
      code: code,
      code_verifier: verifier,
    });

    var res;
    try {
      res = await fetch(this.cfg.tokenEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
    } catch (e) { return { ok: false, reason: 'network' }; }

    if (!res.ok) return { ok: false, reason: 'token_exchange_failed_' + res.status };
    var json = await res.json();
    var tokens = saveTokens(json);
    return { ok: true, tokens: tokens, returnTo: get(KEY_RETURN) };
  };

  /** Refresh without bouncing the member through a login screen. */
  Auth.prototype.refresh = async function () {
    var t = readTokens();
    if (!t || !t.refresh_token) return { ok: false, reason: 'no_refresh_token' };
    var body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: this.cfg.clientId,
      refresh_token: t.refresh_token,
    });
    var res;
    try {
      res = await fetch(this.cfg.tokenEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
    } catch (e) {
      /* NOT a sign-out. Captive wifi, a dead proxy or a dropped connection
         refused nothing — throwing the session away here would make a member
         on a bad connection re-authenticate for no reason. */
      return { ok: false, reason: 'network' };
    }
    if (!res.ok) {
      /* REFUSED, as opposed to unreachable. 400 and 401 are the provider
         saying this refresh token is no longer good; anything else (a 500, a
         gateway) may well work on the next try. Only a refusal clears the
         session, and it must clear it, or isSignedIn() keeps reporting a
         member who can no longer call anything. */
      if (res.status === 400 || res.status === 401) {
        drop(KEY_TOKENS);
      }
      return { ok: false, reason: 'refresh_failed_' + res.status };
    }
    /* A 2xx IS NOT A PROMISE OF JSON.
       An edge interstitial, a captive portal sign-in page, a 204 with no body:
       all arrive with res.ok true and a body JSON.parse refuses. This was the
       one res.json() in this file that was not wrapped, so instead of
       returning a result it THREW, and the throw travelled up accessToken()
       -> api() -> load() to the single .then() at boot, which had no .catch().
       render() was never called and the member kept the server's boot skeleton
       — the blank purple page reported after signing in.

       A body we cannot read is a refresh that did not happen. It is reported
       like any other failure, and deliberately NOT treated as a refusal: the
       provider never said this token was bad, so the session is left alone. */
    var json = null;
    try { json = await res.json(); } catch (e) {}
    if (!json || !json.access_token) return { ok: false, reason: 'refresh_unreadable' };
    // A refresh response may omit the refresh token; keep the existing one.
    if (!json.refresh_token) json.refresh_token = t.refresh_token;
    /* Shopify does NOT reissue an id_token on refresh. The old one is carried
       forward only so `claims()` can still show who signed in — it is stale and
       must never be used to authenticate an API call. */
    if (!json.id_token) json.id_token = t.id_token;
    return { ok: true, tokens: saveTokens(json) };
  };

  /**
   * A valid ID TOKEN, refreshing first if it is close to expiry.
   *
   * This — not the access token — is what our own backend authenticates with.
   * Shopify's Customer Account API access token is opaque: it has three
   * dot-separated segments so it LOOKS like a JWS, but its first segment is not
   * a JWS header and it cannot be verified against the JWKS. The ID token is a
   * real RS256 JWS (kid `id_0`) signed by the key the discovery document
   * publishes, which is why the server can verify it and take `sub` from it.
   *
   * Sending the access token here produced a confident, correct-looking 401:
   * the session was valid, the backend was right to reject it, and nothing in
   * either log said which of the two tokens was wrong.
   */
  Auth.prototype.idToken = async function () {
    var t = readTokens();
    if (!t) return null;
    if (isExpired(t, 60)) {
      var r = await this.refresh();
      if (!r.ok) return null;
      t = r.tokens;
    }
    return t.id_token || null;
  };

  /** A valid ACCESS token — for calling Shopify's Customer Account API, not ours. */
  Auth.prototype.accessToken = async function () {
    var t = readTokens();
    if (!t) return null;
    if (isExpired(t, 60)) {
      var r = await this.refresh();
      if (!r.ok) return null;
      t = r.tokens;
    }
    return t.access_token;
  };

  Auth.prototype.claims = function () {
    var t = readTokens();
    return t ? readIdTokenClaims(t.id_token) : null;
  };

  /**
   * Sign out. Clears local tokens FIRST so a failed redirect still leaves the
   * browser signed out locally, then ends the Shopify session.
   */
  Auth.prototype.signOut = function () {
    var t = readTokens();
    var idToken = t && t.id_token;
    drop(KEY_TOKENS); drop(KEY_VERIFIER); drop(KEY_STATE); drop(KEY_RETURN);
    var q = new URLSearchParams();
    if (idToken) q.set('id_token_hint', idToken);
    if (this.cfg.logoutRedirectUri) q.set('post_logout_redirect_uri', this.cfg.logoutRedirectUri);
    global.location.assign(this.cfg.logoutEndpoint + (q.toString() ? '?' + q.toString() : ''));
  };

  /**
   * Authenticated call to OUR training backend through the Shopify app proxy.
   *
   * The proxy adds the signature Shopify requires; we add the customer token.
   * The backend verifies BOTH and takes the customer id from the token, so
   * nothing this client sends can change whose records are touched.
   *
   * Returns { ok, status, data, reason }. `reason: 'not_connected'` is a
   * truthful state, not an error to hide: until the backend is deployed there
   * is nowhere to save, and the UI must say so rather than imply success.
   */
  Auth.prototype.api = async function (path, options) {
    var opts = options || {};
    var base = this.cfg.apiBase;
    if (!base) return { ok: false, status: 0, reason: 'not_connected' };

    /* Send the ACCESS token, not the ID token.
       Shopify's refresh response does NOT reissue an id_token — verified against
       the live token endpoint, it returns only access_token, expires_in,
       refresh_token and token_type. So the ID token cannot be renewed and dies
       ~20 minutes after sign-in, while the access token refreshes for as long as
       the member stays signed in. The backend verifies the access token by
       asking Shopify's Customer Account API who it belongs to. */
    /* NOTHING HERE MAY WAIT FOREVER.
       Both of the awaits below used to be unbounded. A member on a stalled
       connection — captive wifi, a dead proxy, a request that is accepted and
       never answered — sat on a loading skeleton with no message, no retry and
       no way out, because the screen is painted BEFORE the route is awaited and
       a route that never settles never repaints. That is what a tester hit on
       #resume: header rendered, one skeleton bar, forever.

       A timeout is not an error to hide. It returns the same shape as any
       other failure, so the caller falls back to what it has locally and the
       member keeps moving. */
    var budget = typeof opts.timeoutMs === 'number' ? opts.timeoutMs : 12000;
    var timedOut = function () { return { ok: false, status: 0, reason: 'timeout' }; };

    /* The token refresh is a network call of its own and hangs the same way. */
    var token = await Promise.race([
      this.accessToken(),
      new Promise(function (r) { setTimeout(function () { r('__timeout__'); }, budget); })
    ]);
    if (token === '__timeout__') return timedOut();
    if (!token) return { ok: false, status: 401, reason: 'not_signed_in' };

    var res;
    var ctrl = null;
    try { ctrl = typeof AbortController === 'function' ? new AbortController() : null; } catch (e) {}
    var abortTimer = ctrl ? setTimeout(function () { try { ctrl.abort(); } catch (e) {} }, budget) : null;
    try {
      res = await fetch(base.replace(/\/$/, '') + path, {
        method: opts.method || 'GET',
        headers: Object.assign({
          'Authorization': 'Bearer ' + token,
          'Content-Type': 'application/json'
        }, opts.headers || {}),
        body: opts.body ? JSON.stringify(opts.body) : undefined,
        credentials: 'same-origin',
        signal: ctrl ? ctrl.signal : undefined
      });
    } catch (e) {
      /* An abort and a dead network are different things to a reader of logs,
         and the member is told the same thing either way. */
      var aborted = e && (e.name === 'AbortError' || e.code === 20);
      return aborted ? timedOut() : { ok: false, status: 0, reason: 'network' };
    } finally {
      if (abortTimer) clearTimeout(abortTimer);
    }

    var data = null;
    try { data = await res.json(); } catch (e) {}
    return { ok: res.ok, status: res.status, data: data,
             reason: res.ok ? null : (data && data.error) || ('http_' + res.status) };
  };

  global.BFTrainingAuth = Auth;
})(window);
